import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { FAILED, IDLE, LOADING, NEEDS_REFRESH, SUCCEEDED } from "./constants";
import {
  getAllBankInformation,
  saveAllBankInformation,
} from "../../utils/api/BankAccountApi";
import { RootState } from "../store/store";

import AssociatedBankAccount from "../../interfaces/AssociatedBankAccount";
import { getOktaUser } from "../../utils/LocalStorage";
import {
  getAllParentAccounts,
  getParentAccountTypes,
} from "../../utils/api/ParentAccountsApi";

export interface BankInformationState {
  bankInformation: any[];
  accountTypes: any;
  selectedUnit: any;
  selectedUnitStatus: string;
  bulkUploadStatus: string;
  saveAllStatus: string;
  fetchAllBankInformationStatus: string;
  status: string;
  error: any;
  lastUpdated: string;
}

const initialState: BankInformationState = {
  bankInformation: [],
  accountTypes: null,
  selectedUnit: null,
  selectedUnitStatus: IDLE,
  bulkUploadStatus: IDLE,
  saveAllStatus: IDLE,
  fetchAllBankInformationStatus: IDLE,
  status: IDLE,
  error: null,
  lastUpdated: "",
};

export const saveAllModifiedAccounts = createAsyncThunk(
  "/save-all",
  async (saveAll: any, thunkAPI) => {
    const userName = getOktaUser()[2];
    const body: any = { userName, saveAll };

    let response;

    const importArr: any = [];
    importArr.push(saveAll);

    response = await saveAllBankInformation(body);
    return response;
  }
);
export const fetchAllBankInformation = createAsyncThunk(
  "/bank-accounts",
  async (param, thunkAPI) => {
    const bankInformation: any = await getAllBankInformation();
    let parentBankAccounts = await getAllParentAccounts();
    let accountType = await getParentAccountTypes();
    parentBankAccounts = parentBankAccounts.items;
    for (const element of bankInformation.bankInformation) {
      element.bankAccountAssoc.businessUnitStartDate = gridDateFormatter(
        element.bankAccountAssoc.businessUnitStartDate
      );
      element.bankAccountAssoc.businessUnitEndDate = gridDateFormatter(
        element.bankAccountAssoc.businessUnitEndDate
      );
      while (element.bankAccountAssoc.businessUnitNum.length < 10) {
        element.bankAccountAssoc.businessUnitNum =
          "0" + element.bankAccountAssoc.businessUnitNum;
      }
    }

    let associatedBankAccounts = associateBankAccounts(
      bankInformation,
      parentBankAccounts
    );

    return {
      associatedBankAccounts,
      accountType,
    };
  }
);

function gridDateFormatter(stringDate: string) {
  if (stringDate == null) {
    return "";
  }
  let date = new Date(stringDate);
  const options = {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: "UTC",
  };
  // @ts-ignore
  return date.toLocaleDateString("en-CA", options);
}

function associateBankAccounts(bankInfo: any, parentAccounts: any) {
  let fullyAssociatedAccounts: AssociatedBankAccount[] = [];
  let associatedAccount: AssociatedBankAccount;
  for (const element of bankInfo.bankInformation) {
    associatedAccount = {
      ...element.bankAccountAssoc,
      AssociatedAccounts: Array(),
    };
    let assocAccountsList = element.bankAccountAssocIdListForBU;
    if (assocAccountsList !== null && assocAccountsList !== undefined) {
      for (const bankAccountAssocId of assocAccountsList) {
        for (const i of bankInfo.bankInformation) {
          if (i.bankAccountAssoc.bankAccountAssocId == bankAccountAssocId) {
            let parentAccount = parentAccounts.find((element: any) => {
              if (element.id === i.bankAccountAssoc.bankAccountId) {
                return element;
              }
            });
            if (parentAccount !== undefined) {
              associatedAccount.parentBankAccountName = parentAccount.name;
            }
            associatedAccount.AssociatedAccounts.push(i.bankAccountAssoc);
          }
        }
      }
    }
    fullyAssociatedAccounts.push(associatedAccount);
  }

  return fullyAssociatedAccounts;
}
export const bankInformationSlice = createSlice({
  name: "BankInformation",
  initialState,
  reducers: {
    alertRefreshBankInformation: (state) => {
      state.fetchAllBankInformationStatus = NEEDS_REFRESH;
    },
    updateBankInformation: (state, action) => {
      state.bankInformation = action.payload;
    },
    resetBulkUploadStatus: (state) => {
      state.bulkUploadStatus = IDLE;
    },
    resetSaveAllStatus: (state) => {
      state.saveAllStatus = IDLE;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchAllBankInformation.pending, (state, action) => {
        state.fetchAllBankInformationStatus = LOADING;
      })
      .addCase(fetchAllBankInformation.fulfilled, (state, action) => {
        state.fetchAllBankInformationStatus = SUCCEEDED;
        state.bankInformation = action.payload.associatedBankAccounts;
        state.accountTypes = action.payload.accountType;
      })
      .addCase(fetchAllBankInformation.rejected, (state, action) => {
        state.fetchAllBankInformationStatus = FAILED;
        state.error = action.error.message;
      })
      .addCase(saveAllModifiedAccounts.pending, (state, action) => {
        state.saveAllStatus = LOADING;
      })
      .addCase(saveAllModifiedAccounts.fulfilled, (state, action) => {
        state.saveAllStatus = SUCCEEDED;
      })
      .addCase(saveAllModifiedAccounts.rejected, (state, action) => {
        state.saveAllStatus = FAILED;
        state.error = action.error.message;
      });
  },
});

export default bankInformationSlice.reducer;
export const { alertRefreshBankInformation, resetSaveAllStatus } =
  bankInformationSlice.actions;
export const bankInformationSelector = (state: RootState) =>
  state.bankInformation.bankInformation;

export const accountTypesSelector = (state: RootState) =>
  state.bankInformation.accountTypes;
